import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Space, Tooltip } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { MessageStates } from 'containers/MessageBox';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { getDataLogStatusLabel } from 'utils/display-enum-label';
import styled, { createGlobalStyle } from 'styled-components';

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 130px);
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  .top-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .bottom-section {
    flex-shrink: 0;
    padding: 16px 0;
    margin-top: auto;
  }
`;

const StatusListWrapper = styled.div<{ deduct: number }>`
  max-height: calc(100vh - ${({ deduct }) => deduct}px);
  min-height: 45px;
  overflow-y: auto;
  background: var(--color-surface-base, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 0 8px 12px 8px;
  margin-bottom: 15px;
  /* Thinner, modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 8px;
  user-select: none;
  padding: 6px 0;
  border-bottom: 1px solid var(--color-border, #e8f0f0);
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const StatusItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const StatusItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  font-weight: 500;
`;

const CustomCheckboxSizeStyle = createGlobalStyle`
  /* Only remove bottom padding when this specific form is in the drawer */
  .ant-drawer-body:has(.board-status-wrapper) {
    padding-bottom: 0 !important;
  }
`;

interface BoardStatusSelectorProps {
  visibleStatuses: string[];
  statusOrder?: string[];
  onSave: (config: {
    visibleStatuses: string[];
    statusOrder: string[];
  }) => void;
  onClose?: () => void;
}

const ALL_STATUSES = [
  { value: DataLogStatus.NOT_STARTED },
  { value: DataLogStatus.SAVED },
  { value: DataLogStatus.AWAITING_APPROVAL },
  { value: DataLogStatus.INTERNAL_APPROVED },
  { value: DataLogStatus.APPROVED },
  { value: DataLogStatus.AWAITING_CLIENT_APPROVAL },
  { value: DataLogStatus.CLIENT_APPROVED },
  { value: DataLogStatus.LOCKED },
  { value: DataLogStatus.SUBMITTED },
];

export const BoardStatusSelector: React.FC<BoardStatusSelectorProps> = ({
  visibleStatuses,
  statusOrder,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Use local state to track checked statuses - changes are not saved until Save is clicked
  const [localVisibleStatuses, setLocalVisibleStatuses] =
    React.useState<string[]>(visibleStatuses);
  const [orderedStatuses, setOrderedStatuses] =
    React.useState<typeof ALL_STATUSES>(ALL_STATUSES);

  // Initialize status order
  React.useEffect(() => {
    setLocalVisibleStatuses(visibleStatuses);

    // Initialize order based on saved preferences or default order
    if (statusOrder && statusOrder.length > 0) {
      const ordered = statusOrder
        .map((value) => ALL_STATUSES.find((status) => status.value === value))
        .filter(
          (status): status is (typeof ALL_STATUSES)[0] => status !== undefined
        );
      // Add any statuses not in the order at the end
      const unordered = ALL_STATUSES.filter(
        (status) => !statusOrder.includes(status.value)
      );
      setOrderedStatuses([...ordered, ...unordered]);
    } else {
      setOrderedStatuses(ALL_STATUSES);
    }
  }, [visibleStatuses, statusOrder]);

  const handleToggle = (status: string, checked: boolean) => {
    // Only update local state - don't save yet
    if (checked) {
      setLocalVisibleStatuses((prev) => [...prev, status]);
    } else {
      setLocalVisibleStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...orderedStatuses];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    setOrderedStatuses(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === orderedStatuses.length - 1) return;
    const newOrder = [...orderedStatuses];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    setOrderedStatuses(newOrder);
  };

  const handleSave = () => {
    // Save all changes including order
    const finalOrder = orderedStatuses.map((status) => status.value);
    onSave({
      visibleStatuses: localVisibleStatuses,
      statusOrder: finalOrder,
    });

    // Show success message
    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.status-preferences-saved'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));

    // Close the drawer if onClose is provided
    if (onClose) {
      onClose();
    }
  };

  // Check if there are any changes (visibility or order)
  const hasChanges =
    JSON.stringify([...localVisibleStatuses].sort()) !==
      JSON.stringify([...visibleStatuses].sort()) ||
    JSON.stringify(orderedStatuses.map((s) => s.value)) !==
      JSON.stringify(
        statusOrder && statusOrder.length > 0
          ? statusOrder
          : ALL_STATUSES.map((s) => s.value)
      );

  return (
    <>
      <CustomCheckboxSizeStyle />
      <Wrapper className="board-status-wrapper">
        <div className="top-section">
          <StatusListWrapper
            deduct={180}
            tabIndex={0}
            role="region"
            aria-label={t('submissions.board-status-settings-title')}
          >
            <div style={{ paddingTop: 12 }}>
              {orderedStatuses.map((status, index) => {
                const isVisible = localVisibleStatuses.includes(status.value);
                return (
                  <StatusItem key={status.value}>
                    <StatusItemLeft
                      onClick={() => handleToggle(status.value, !isVisible)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Tooltip
                        title={
                          isVisible
                            ? t('common.visible-uncheck-to-hide')
                            : t('common.hidden-check-to-show')
                        }
                      >
                        <ScopeCheckbox
                          compact
                          checked={isVisible}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggle(status.value, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginRight: 8 }}
                        />
                      </Tooltip>
                      <StatusText>
                        {getDataLogStatusLabel(status.value, t)}
                      </StatusText>
                    </StatusItemLeft>
                    <StatusItemRight onClick={(e) => e.stopPropagation()}>
                      <Space size="small" style={{ gap: '4px' }}>
                        <Tooltip title={t('submissions.move-status-up')}>
                          <button
                            type="button"
                            aria-label={t('submissions.move-status-up')}
                            disabled={index === 0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMoveUp(index);
                            }}
                            style={{
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              color:
                                index === 0
                                  ? 'var(--color-text-disabled, #d9d9d9)'
                                  : 'var(--color-text-muted, #5a6a6a)',
                              fontSize: 12,
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            <ArrowUpOutlined aria-hidden />
                          </button>
                        </Tooltip>
                        <Tooltip title={t('submissions.move-status-down')}>
                          <button
                            type="button"
                            aria-label={t('submissions.move-status-down')}
                            disabled={index === orderedStatuses.length - 1}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMoveDown(index);
                            }}
                            style={{
                              cursor:
                                index === orderedStatuses.length - 1
                                  ? 'not-allowed'
                                  : 'pointer',
                              color:
                                index === orderedStatuses.length - 1
                                  ? 'var(--color-text-disabled, #d9d9d9)'
                                  : 'var(--color-text-muted, #5a6a6a)',
                              fontSize: 12,
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            <ArrowDownOutlined aria-hidden />
                          </button>
                        </Tooltip>
                      </Space>
                    </StatusItemRight>
                  </StatusItem>
                );
              })}
            </div>
          </StatusListWrapper>
        </div>
        <div className="bottom-section">
          <ScopeButton
            type="primary"
            width="100%"
            height="45px"
            disabled={!hasChanges}
            onClick={handleSave}
          >
            {t('submissions.save-status-settings')}
          </ScopeButton>
        </div>
      </Wrapper>
    </>
  );
};
